import React, {useEffect, useState} from 'react';
import { withRouter } from "react-router";
import poweredByDq from '../../../assets/images/PoweredByDQ_Default.png';
import LoaderWithBackDrop from "../../../components/LoaderWithBackDrop/LoaderWithBackDrop.comp";
import {confirmConsent, getConsentRequest} from "../../../services/consent.service";

/*
 * Parental consent (Case 2) — where the "I consent and activate" button of the
 * LP-01/C2 email lands: /consent?consent_token=…
 *
 * Opening the page only READS the request. Consent is recorded when the parent
 * ticks the box and presses the button — never by the link alone, so a mail
 * scanner that follows links cannot consent on a parent's behalf.
 *
 * Copy: "Parental consent page" (Ian Ryu, 6 Oct 2026). English only for now.
 * Look: the consent email's own (branding.js in the API) — Powered by DQ mark,
 * the orange button, the same text colours — so the page reads as its next step.
 */

const VIEW = {
    LOADING: 'loading',
    CONSENT: 'consent',
    SUCCESS: 'success',
    ALREADY: 'already',
    EXPIRED: 'expired',
    INVALID: 'invalid',
};

const DPO_EMAIL = 'dpo@dqforall.com';

/** "13 October 2026" */
const formatDeadline = (value) => {
    const date = value ? new Date(value) : null;
    if (!date || isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-GB', {day: 'numeric', month: 'long', year: 'numeric'});
};

const ParentalConsent = (props) => {

    const {location} = props;
    const consentToken = new URLSearchParams(location.search).get('consent_token') || '';

    const [view, setView] = useState(VIEW.LOADING);
    const [request, setRequest] = useState({});
    const [agreed, setAgreed] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [message, setMessage] = useState('');

    /** A refused link: expired (the 7 days ran out), or simply not usable. */
    const showRefusal = (error) => {
        const data = (error && error.data) || {};
        if (data.expired) {
            setRequest({child_first_name: data.child_first_name, school_name: data.school_name});
            setView(VIEW.EXPIRED);
            return;
        }
        setMessage((error && error.message) || '');
        setView(VIEW.INVALID);
    };

    useEffect( () => {
        if (!consentToken) {
            setView(VIEW.INVALID);
            return;
        }
        getConsentRequest(consentToken)
            .then((res) => {
                const data = (res && res.data) || {};
                setRequest(data);
                setView(data.already_consented ? VIEW.ALREADY : VIEW.CONSENT);
            })
            .catch(showRefusal);
    }, [] )

    const submit = () => {
        if (!agreed || submitting) return;
        setSubmitting(true);
        setMessage('');
        confirmConsent(consentToken)
            .then((res) => {
                const data = (res && res.data) || {};
                setView(data.already_consented ? VIEW.ALREADY : VIEW.SUCCESS);
            })
            .catch((error) => {
                // A link that stopped being usable while the page was open.
                if (error && error.data && error.data.valid === false) {
                    showRefusal(error);
                    return;
                }
                setMessage((error && error.message) || 'We could not record your consent. Please try again.');
            })
            .finally(() => setSubmitting(false));
    };

    const child = request.child_first_name || 'your child';
    const school = request.school_name || 'your school';
    const links = request.links || {};
    const deadline = formatDeadline(request.expires_at);
    const documentLinks = [
        ['Information Letter', links.information_letter_url],
        ['Terms of Use', links.terms_url],
        ['Privacy Policy', links.privacy_policy_url],
    ].filter(([, url]) => !!url);

    if (view === VIEW.LOADING) return <LoaderWithBackDrop loading={true}/>;

    return (
        <div className="consent-shell">
        <div className="consent-page">
            <div className="consent-header">
                <img src={poweredByDq} alt="Powered by DQ" />
            </div>

            {view === VIEW.CONSENT && (
                <>
                    <div className="consent-title">Give consent for {child}</div>
                    <p className="consent-text">
                        {school} has invited {child} to take part in the Child Online Safety (COS) Assessment and
                        online safety learning programs from Powered by DQ.
                    </p>
                    <p className="consent-text">
                        Give consent{deadline ? ` by ${deadline}` : ''} to keep {child}'s account open. If consent isn't
                        given by then, the account closes and {child}'s personal details are deleted.
                    </p>
                    <label className="consent-check">
                        <input
                            type="checkbox"
                            checked={agreed}
                            onChange={(e) => setAgreed(e.target.checked)}
                        />
                        <span>
                            I have read the Information Letter, Terms of Use and Privacy Policy, and I agree
                            to {child} taking part and to the processing of their data as described.
                        </span>
                    </label>
                    <div className="consent-action">
                        <button
                            className="consent-btn"
                            disabled={!agreed || submitting}
                            onClick={submit}
                        >
                            I consent and activate
                        </button>
                    </div>
                    {message && <p className="consent-error">{message}</p>}
                    {documentLinks.length > 0 && (
                        <p className="consent-links">
                            {documentLinks.map(([label, url]) => (
                                <a key={label} href={url} target="_blank" rel="noopener noreferrer">{label}</a>
                            ))}
                        </p>
                    )}
                    <p className="consent-small">
                        You can withdraw consent at any time by emailing <a href={`mailto:${DPO_EMAIL}`}>{DPO_EMAIL}</a>.
                        Withdrawing won't affect {child}'s standing at school.
                    </p>
                </>
            )}

            {view === VIEW.SUCCESS && (
                <>
                    <div className="consent-title">Thank you. {child}'s account is active.</div>
                    <p className="consent-text">
                        {child} can keep signing in with their existing account details. You can close this page.
                    </p>
                    <p className="consent-small">
                        You can withdraw consent at any time by emailing <a href={`mailto:${DPO_EMAIL}`}>{DPO_EMAIL}</a>.
                    </p>
                </>
            )}

            {view === VIEW.ALREADY && (
                <>
                    <div className="consent-title">Consent already given</div>
                    <p className="consent-text">You've already given consent for {child}.</p>
                </>
            )}

            {view === VIEW.EXPIRED && (
                <>
                    <div className="consent-title">This link has expired</div>
                    <p className="consent-text">
                        The 7-day consent period has ended and {child}'s account has been closed. To take part,
                        contact {school} to add {child} again.
                    </p>
                </>
            )}

            {view === VIEW.INVALID && (
                <p className="consent-text">
                    {message || 'This consent link is not valid. Please check the link in your email, or contact your school.'}
                </p>
            )}

            {/* The email closes on the Powered by DQ mark; so does the page. */}
            <div className="consent-footer">
                <img src={poweredByDq} alt="Powered by DQ" />
            </div>
        </div>
        </div>
    );
};

export default withRouter(ParentalConsent);
